import { EnWritingResult, Question, Stroke, Stroke2Path, isEnWritingResult, isSlideComponentHkcsEnQ1, isSlideComponentHkcsEnQ2 } from '@/types/slide';
import { Box, Card, CardActionArea, CardContent, Typography } from '@mui/material';
import Konva from 'konva';
import { ShapeConfig } from 'konva/lib/Shape';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Group, Layer, Path, Stage } from 'react-konva';

type AnimatedStroke = {
    coordinates: Stroke;
    persist?: boolean;
    color?: ShapeConfig['stroke'];
};

type ResultModel = EnWritingResult & { align?: string };

function parseQuestion(question: Question, type?: string): ResultModel | undefined {
    if (isSlideComponentHkcsEnQ1(question)) {
        if (question.content.phrase) {
            const data = question.answers?.[0]?.data;
            const answer = data ? JSON.parse(data) : undefined;

            const ret = { phrase: question.content.phrase, writing: answer?.hkcsEnQ1?.answer, align: 'center' };
            return ret;
        }
    } else if (isSlideComponentHkcsEnQ2(question)) {
        const data = question.answers?.[0]?.data;
        const answer = data ? JSON.parse(data) : undefined;

        if (type == 'answer') {
            const ret = { phrase: answer?.hkcsEnQ2?.textContent || '', writing: answer?.hkcsEnQ2?.answer };
            return ret;
        } else if (type == 'sampleSentenceAnswer') {
            const ret = { phrase: answer?.hkcsEnQ2?.sampleSentence || '', writing: answer?.hkcsEnQ2?.sampleSentenceAnswer };
            return ret;
        }
    }

    return undefined;
}

export function ResultDisplayEnWriting({ question, showReference, type, viewOnly }: { question: Question; showReference?: boolean; type?: string; viewOnly: boolean }) {
    const resultModel = parseQuestion(question, type);

    const result = resultModel && isEnWritingResult(resultModel) ? resultModel : undefined;

    const strokes: AnimatedStroke[][] =
        result?.writing.map((writingItem) =>
            writingItem.normalMode === true
                ? writingItem.strokes.reduce((strokes, history) => {
                      history.forEach((coordinates, index) => {
                          if (index === history.length - 1) {
                              strokes.push({ coordinates, persist: true });
                          } else {
                              strokes.push({ coordinates, persist: false, color: 'red' });
                          }
                      });
                      return strokes;
                  }, [] as AnimatedStroke[])
                : writingItem.strokes.map((coordinates) => ({ coordinates })),
        ) ?? [];

    // ui state
    const strokesRefs = useRef<{ stroke: AnimatedStroke; path: Konva.Path; tween?: Konva.Tween }[][]>([]);

    const mainRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLDivElement>(null);

    const [size, setSize] = useState({ width: 0, height: 0 });
    const [textHeight, setTextHeight] = useState(0);

    useEffect(() => {
        if (mainRef.current) {
            setSize({
                width: mainRef.current.offsetWidth ?? mainRef.current.getBoundingClientRect().width,
                height: mainRef.current.offsetHeight ?? mainRef.current.getBoundingClientRect().height,
            });
        }
    }, []);

    useLayoutEffect(() => {
        if (textRef.current && result?.phrase) {
            const height = Math.max(textRef.current.offsetHeight, textRef.current.getBoundingClientRect().height);
            setTextHeight(height);
        }
    }, [result?.phrase]);

    const replay = useCallback(async () => {
        // reset all stroke and setup new tweens
        strokesRefs.current.forEach((strokeRef) => {
            strokeRef.forEach((ref) => {
                // clear all paths
                const pathLen = ref.path.getLength();
                ref.path.dashOffset(pathLen);
                ref.path.dash([pathLen]);
                ref.path.visible(false);

                // stop all previous tweens before playing the new one
                if (ref.tween) {
                    ref.tween.reset();
                }

                // create a new tween for each path
                ref.tween = new Konva.Tween({
                    node: ref.path,
                    dashOffset: 0,
                    duration: 0.5,
                });
            });
        });

        // play all new tweens in sequence
        for (const strokeRef of strokesRefs.current) {
            for (const stroke of strokeRef) {
                const animation = new Promise<void>((resolve, reject) => {
                    if (stroke.tween) {
                        stroke.tween.onReset = () => reject();
                        stroke.tween.onFinish = () => resolve();
                        /**
                         * +1 is to workaround if using round lineCap,
                         * some path would have crop a bit of the offset'ed dash hence this workaround
                         *
                         * can remove the +1 length and the visible toggle if not using round lineCap
                         */
                        stroke.path.dash([stroke.path.getLength() + 1]);
                        // hide it and reval it only before animation is the same reason as above
                        stroke.path.visible(true);
                        stroke.tween.play(); // execute the tween
                    } else {
                        resolve();
                    }
                });

                try {
                    await animation;
                } catch {
                    // we might cancel the animation e.g. user click replay again
                    break;
                } finally {
                    // clear it only if persiste is set explicitly to false
                    if (stroke.stroke.persist === false) {
                        stroke.path.visible(false);
                    }
                }
            }
        }
    }, []);

    const scrollBarWidth = resultModel?.align == 'center' ? 2 : 20; // 2 for border width

    let strokesOriginX = 0;
    let strokesOriginY = 0;
    return (
        <Card
            data-mui-color-scheme='light'
            sx={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                p: 0,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                position: 'relative',
                backgroundColor: 'white !important', //not sure why but need !important for Cards
                display: 'flex',
            }}
            data-testid='result-display-writing'
            ref={mainRef}
        >
            <CardActionArea
                sx={{
                    cursor: !viewOnly ? 'pointer' : 'default',
                    maxHeight: '100%',
                    my: 'auto', // Centers when content is short
                    overflowX: 'hidden',
                    overflowY: !viewOnly ? 'auto' : 'hidden',

                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                    '&.Mui-focusVisible': {
                        backgroundColor: 'transparent',
                    },
                    '& .MuiCardActionArea-focusHighlight': {
                        opacity: '0 !important',
                    },
                }}
            >
                <CardContent
                    sx={{
                        width: `${size.width - scrollBarWidth}px`,
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            maxHeight: '100%',

                            display: 'flex',
                            alignItems: 'flex-start',
                        }}
                    >
                        {showReference && (
                            <Typography
                                ref={textRef}
                                sx={{
                                    fontFamily: 'TTComicBold',
                                    fontSize: '36px',
                                    width: `${size.width - scrollBarWidth}px`,
                                    textAlign: resultModel?.align == 'center' ? 'center' : 'left',
                                    color: '#555',
                                    opacity: 0.35,
                                }}
                            >
                                {result?.phrase}
                            </Typography>
                        )}
                    </Box>

                    <style>
                        {`
                          .static-konva .konvajs-content {
                            position: static !important;
                          }
                        `}
                    </style>

                    <div className='static-konva'>
                        <Stage
                            width={size.width - scrollBarWidth}
                            height={textHeight}
                            onClick={async () => {
                                if (!viewOnly) {
                                    await replay();
                                }
                            }}
                        >
                            <Layer>
                                {/* <Rect x={0} y={0} width={size.width - scrollBarWidth} height={textHeight} stroke='red' strokeWidth={1} listening={false} /> */}
                                {strokes?.map((writingStrokes, writingIndex) => {
                                    const writingItem = result?.writing[writingIndex];

                                    //jacky: hard-coded scale for fontSize 36px
                                    //TODO: actually come up with something to calculate the scale
                                    const scale = 0.252;

                                    const width = writingItem?.width ? writingItem.width : 0;
                                    // const height = writingItem?.height ? writingItem.height : 0;

                                    const scaledWidth = (width * scale) / 2;
                                    // const scaledHeight = (height * scale) / 2;

                                    let originX = (size.width - scaledWidth) / 2;

                                    if (resultModel?.align != 'center' && writingItem?.width) {
                                        if (strokesOriginX + scaledWidth > size.width - scrollBarWidth) {
                                            originX = 0;
                                            strokesOriginX = 0;
                                            // TODO: properly calculate the width of line height
                                            strokesOriginY += 52;
                                        } else {
                                            originX = strokesOriginX;
                                        }
                                        // jacky: 50 is the width of white space between words, again measured not calculated
                                        // TODO: properly calculate the width of white space
                                        strokesOriginX += scaledWidth + 50 * scale;
                                    }

                                    const origin = { x: originX, y: strokesOriginY };

                                    return (
                                        <Group key={writingIndex}>
                                            {/* <Rect x={origin.x} y={origin.y} width={scaledWidth} height={scaledHeight} stroke='black' strokeWidth={1} listening={false} /> */}
                                            {writingStrokes.map((stroke, strokeIndex) => {
                                                const data = Stroke2Path(stroke.coordinates, scale, origin);
                                                return (
                                                    <Path
                                                        key={strokeIndex}
                                                        x={0}
                                                        y={0}
                                                        stroke={stroke.color ?? '#000000'}
                                                        visible={stroke.persist !== false}
                                                        lineJoin='round'
                                                        lineCap='round'
                                                        strokeWidth={6}
                                                        data={data}
                                                        ref={(el) => {
                                                            if (el) {
                                                                if (!strokesRefs.current[writingIndex]) {
                                                                    strokesRefs.current[writingIndex] = [];
                                                                }
                                                                strokesRefs.current[writingIndex]![strokeIndex] = {
                                                                    stroke,
                                                                    path: el,
                                                                };
                                                            }
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Group>
                                    );
                                })}
                            </Layer>
                        </Stage>
                    </div>
                </CardContent>
            </CardActionArea>
        </Card>
    );
}
